import React from "react";
import styled from "@emotion/styled";
import { motion, AnimatePresence } from "framer-motion";
import PropTypes from "prop-types";
import { useMediaQuery } from "react-responsive";
import { Sizes, Colors, Typography } from "../../styles";

import FeaturedCTA from "./FeaturedCTA";
import MobileSiteSearch from "./MobileSiteSearch";

const SubMenu = ({ activeMenuItems, allMenuItems, subMenuIsOpen, expanded, setExpanded, inactiveLanguage }) => {

	const isDesktopOrLaptop = useMediaQuery({
		query: `(min-width: ${Sizes.screens.largeMenu})`,
	});

	const handleSectionBtnClick = (id, isOpen) => {
		event.preventDefault();
		setExpanded(isOpen ? false : id);
	}

	const renderMobileMenu = (menuItemsArray) => {
		if (menuItemsArray.length <= 0) return null;

		return menuItemsArray.map(item => {
			const isOpen = item.id === expanded;

			return (
				<SectionContainer key={item.id}>
					<SectionButton
						dangerouslySetInnerHTML={{__html:item.title}}
						onClick={() => handleSectionBtnClick(item.id, isOpen)}
						className={isOpen ? 'expanded' : null}
					>
					</SectionButton>
					<AnimatePresence initial={false}>
						{ isOpen && (
							<LinkGroupsContainer
								key={item.id}
								initial="collapsed"
								animate="open"
								exit="collapsed"
								variants={{
									open: { opacity: 1, height: "auto" },
									collapsed: { opacity: 0, height: 0 }
								}}
								transition={{ duration: 0.5, ease: [0.04, 0.62, 0.23, 0.98] }}
							>
								<ul>
									<li>
										{renderLinkGroups(item)}
									</li>
								</ul>
							</LinkGroupsContainer>
						)}
					</AnimatePresence>
				</SectionContainer>
			)
		})
	}

	const renderMenu = () => {
		if (!isDesktopOrLaptop) {
			const signInButtonText = inactiveLanguage?.code === "en" ? "Se connecter" : "Sign in";
			const activeLanguage = inactiveLanguage?.code === "en" ? "fr" : "en";
			const signInButtonUrl = (language) => {
				if (language !== "fr") {
					return "https://route.futurpreneur.ca/_forms/default.aspx?ReturnUrl=%2f_layouts%2f15%2fAuthenticate.aspx%3fSource%3d/en-ca";
				}
		
				return "https://route.futurpreneur.ca/_forms/default_fr.aspx?ReturnUrl=%2f_layouts%2f15%2fAuthenticate.aspx%3fSource%3d/fr-ca";
			}
			return (
				<>
					<MobileSiteSearch activeLanguage={activeLanguage} />
					{renderMobileMenu(allMenuItems)}
					<MobileMenuButtonsContainer>
						<LangButton href={inactiveLanguage?.url}>{inactiveLanguage?.code}</LangButton>
						<SignInButton className="button" href={signInButtonUrl(activeLanguage)}>{signInButtonText}</SignInButton>
					</MobileMenuButtonsContainer>
				</>
			);
		} else  {
			return (
				<SectionContainerDesktop
					key={activeMenuItems.id}
					initial="collapsed"
					animate="open"
					exit="collapsed"
					variants={{
						open: { opacity: 1},
						collapsed: { opacity: 0}
					}}
					transition={{duration: 0.5, ease: "easeInOut"}}
					className="submenu-links"
				>
					<FeaturedCTA featuredCta={activeMenuItems.cta}/>
					<ul>
						<li>
							{renderLinkGroups(activeMenuItems)}
						</li>
					</ul>
				</SectionContainerDesktop>
			);
		}
	}

	const renderLinkGroups = (object) => {
		if (Object.keys(object).length <= 0) return null;

		return object.linkGroups.map(item => {
			return (
				<>
					<LinkGroupTitle dangerouslySetInnerHTML={{__html:item.title}}></LinkGroupTitle>
					{
						item.links.length > 0 ?
							<ul>
								{renderLinkGroupItems(item)}
							</ul>
							: null
					}
				</>
			)
		})
	}

	const renderLinkGroupItems = (object) => {
		const renderClasses = (isSpacerProp, isExternalProp) => {
			// we add the class 'isSpacer' to add the space in between menu links
			const isSpacer = isSpacerProp === "1" ? 'isSpacer' : '';
			
			// we add the class 'isExternal' to add the arrow after menu links
			const isExternal = isExternalProp === true ? 'isExternal' : '';
			return `${isSpacer} ${isExternal}`;			
		}

		const targetValue = (isExternalProp) => {	
			if (isExternalProp === true) return '_blank';
			return '_self';
		}

		return object.links.map(item => {

			return (
				<li key={item.id} className={renderClasses(item.is_spacer, item.is_external)}>
					<Link href={item.url} dangerouslySetInnerHTML={{__html:item.title}} target={targetValue(item.is_external)}></Link>
				</li>
			)
		})
	}

	return(
		<AnimatePresence initial={false}>
			{
				subMenuIsOpen && (
					<SubMenuContainer
						key="subMenu"
						initial="collapsed"
						animate="open"
						exit="collapsed"
						variants={
							{open: { height: "auto", overflow: "hidden"},
								collapsed: { height: 0}}
						}
						transition={{ duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }}
						className="main-grid"
					>
						<div className="main-grid__margin"></div>
						<div className="main-grid__body">
							{
								isDesktopOrLaptop && (
									<Divider>
										<img
											src={`${mainScript.assetPath}images/svg/graphics/menu-hr.svg`}
											alt=""
										/>
									</Divider>
								)
							}
							<MenuContainer>
								{renderMenu()}
							</MenuContainer>
						</div>
						<div className="main-grid__margin"></div>
					</SubMenuContainer>
				)}
		</AnimatePresence>
	)
}

SubMenu.propTypes = {
	activeMenuItems: PropTypes.object.isRequired,
	allMenuItems: PropTypes.array.isRequired,
	subMenuIsOpen: PropTypes.bool.isRequired,
	expanded: PropTypes.number.isRequired,
	setExpanded: PropTypes.func.isRequired,
	inactiveLanguage: PropTypes.object.isRequired,
};

export default SubMenu;

const SubMenuContainer = styled(motion.div)`
    background-color: ${Colors.fp.pineGreen10};
    border-radius: 0 0 4rem 4rem;
	background-image: url(${mainScript.assetPath}images/svg/graphics/pale-teal-star.svg), url(${mainScript.assetPath}images/svg/graphics/large-teal-star.svg), url(${mainScript.assetPath}images/svg/graphics/small-teal-star.svg);
    background-repeat: no-repeat, no-repeat, no-repeat;
	background-size: 1.9rem, 3.5rem, 2rem;
	background-position: bottom 8.6rem right 6.7rem, bottom 5.4rem right 3rem, bottom 3rem right 8rem;
	
    @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
		background-position: right 7.5rem bottom 10rem, right 2rem bottom 3rem, right 11.7rem bottom 1rem;
		background-size: unset;
        border-radius: 0 0 6rem 6rem;
        position: absolute;
        width: 100%;
        top: 14rem;
        left: 0;
    }

    ul {
        list-style: none;
        padding: unset;
        margin: unset;
    }

    li {
        margin: unset;
    }
`;

const Divider = styled.div`
    @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
        padding: 0;
    }

    img {
        display: block;
        width: 100%;
    }
`;

const SectionContainer = styled.div`
    margin-bottom: 2.5rem;

    &:last-of-type {
        margin-bottom: 0;
    }

    li {
        margin-bottom: 1rem;

        &:last-of-type {
            margin-bottom: 0;
        }
    }

`;

const SectionButton = styled.a`
    padding: unset;
    background-color: unset;
    color: ${Colors.base.black};
    font-size: 2.4rem;
    line-height: 2.5rem;
    letter-spacing: -0.5%;
    position: relative;
    display: inline-block;
	font-family: ${Typography.fontFamily.base};
	font-weight: ${Typography.fontWeight.bold};

    &::after {
        content: '';
        background-image: url(${`${mainScript.assetPath}images/svg/icons/down-arrow-green.svg`});
        position: absolute;
        top: 0.8rem;
        right: -3rem;
        width: 2rem;
        height: 1.3rem;
        transform: rotate(0);
        transition: transform .3s ease-in-out;
    }

    &.expanded {
        &::after {
            transform: rotate(180deg);
        }
    }
`;

const LinkGroupTitle = styled.p`
    font-size: 1.4rem;
    font-weight: ${Typography.fontWeight.black};
    line-height: 3rem;
    margin-top: 2.5rem;
    margin-bottom: 1rem;
	font-family: ${Typography.fontFamily.base};

    &:first-of-type {
        margin-top: 2rem;

		@media only screen and (min-width: ${Sizes.screens.largeMenu}) {
			margin-top: unset;
		}
    }

    @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
        font-size: 22px;
        margin-bottom: 2.5rem;
		margin-top: 5rem;
    }
`;

const LinkGroupsContainer = styled(motion.div)`
    overflow: hidden;

	li.isSpacer {
		margin-bottom: 0;
		height: 1.2rem;
		overflow: hidden;

		a {
			color: transparent;
			pointer-events: none;
		}
	}

	li.isExternal {	
		a::after {
			content: url(${`${mainScript.assetPath}images/svg/icons/button-arrow-green.svg`});
			position: relative;
			top: 0;
			right: -1rem;
		}
	}
`;

const Link = styled.a`
    color: ${Colors.base.black};
    font-size: 1.7rem;
    font-weight: ${Typography.fontWeight.medium};
    line-height: 2.4rem;
	position: relative;

    @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
        font-weight: ${Typography.fontWeight.bold};
		font-size: 2rem;
		line-height: 2.2rem;
		font-family: ${Typography.fontFamily.base};
    }

	&::before {
		content: '';
		position: absolute;
		top: 2.1rem;
		left: 0;
		height: 0.2rem;
		width: 0;
		transition: width .2s ease-in-out;
		background-color: ${Colors.fp.pineGreen};
	}

	&:hover,
	&:focus {
		@media only screen and (min-width: ${Sizes.screens.largeMenu}) {
			&::before {
				content: '';
				width: 100%;
			}
		}
	}
`;

const LangButton = styled.a`
    display: inline-block;
    padding: unset;
    background-color: unset;
    color: ${Colors.base.black};
	text-transform: uppercase;
	font-family: ${Typography.fontFamily.base};
	font-weight: ${Typography.fontWeight.bold};

    &::after {
        content: none;
    }

    &:hover,
    &:focus {
        padding-right: 0;

        &::after {
            content: none;
        }
    }
`;

const SignInButton = styled.a`
    margin-left: 2rem;
`;

const MobileMenuButtonsContainer = styled.div`
    margin-top: 1rem;
`;

const MenuContainer = styled.div`
    padding: 0 0 24rem;

    @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
        padding: 2.5rem 0 4rem;
    }
`;

const SectionContainerDesktop = styled(motion.div)`
    display: grid;
    grid-template-columns: minmax(auto, 431px) 1fr;
    column-gap: 5rem;

	@media only screen and (min-width: 1085px) {
		column-gap: 11rem;
	}

    li {
        @media only screen and (min-width: ${Sizes.screens.largeMenu}) {
            margin-bottom: 1rem;
        }

        &:last-of-type {
            margin-bottom: unset;
        }

		&.isSpacer {
			margin-bottom: 0;
			height: 1.2rem;
			overflow: hidden;
	
			a {
				color: transparent;
				pointer-events: none;
			}
		}
	
		&.isExternal {	
			a::after {
				content: url(${`${mainScript.assetPath}images/svg/icons/button-arrow-green.svg`});
				position: relative;
				top: 0;
				right: -1rem;
			}
		}
    }
`;
